import React, { Suspense } from 'react'
import MuiTextField from '@mui/material/TextField'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-duotone-svg-icons/faDownload'
import { faFile } from '@fortawesome/pro-duotone-svg-icons/faFile'
import { faPlus } from '@fortawesome/pro-duotone-svg-icons/faPlus'
import { faSignature } from '@fortawesome/pro-duotone-svg-icons/faSignature'
import { faUnlink } from '@fortawesome/pro-duotone-svg-icons/faUnlink'

import NewFile from '../../Components/Admin/Pages/Files/NewFile'
import ConfirmDeleteDialog from '../../Components/Form/ConfirmDeleteDialog'
import LoadingDefault from '../../Components/Elements/Loading/LoadingDefault'
import Signature from './FileDrawableField/index'

import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
} from '@/feature/DisplayItems/BaseDisplayItem'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import { Link } from '@mui/material'

const FilePreviewModal = React.lazy(
    () => import('../../Components/Elements/Modals/FilePreviewModal')
)

/* ---------- Types ---------- */

interface FileItem {
    id: string | number
    filename: string
    description?: string | number | null
    mime?: string
}

interface FileFieldSettings {
    file_types?: string[]
    file_preview?: boolean
    preview_direct?: boolean
    preview_width?: string
    preview_height?: string
    max_slider?: number
    multiple?: boolean
    signature?: boolean
}

interface Item extends DisplayItemBase {
    settings?: FileFieldSettings
}

interface Kedo {
    t: (key: string) => string
    api: () => {
        getBaseUrl: () => string
        getFileEndpoint: () => string
    }
    isAdminOrEnvironmentAdmin: () => boolean
    env: () => {
        hasCredential: (cred: string) => boolean
    }
}

interface Props extends BaseDisplayItemProps<FileItem[], Item> {
    kedo: Kedo
    mode: string // includes 'list' in your legacy code
    custom_layout?: string
    dossier?: { id?: string | number }
    onKeyPress?: (
        id: string | number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

interface State {
    value: FileItem[]
    signing: boolean
    deleteFile: string | number | false
    showUpload?: boolean
    dataSignature: string
}

/* ---------- Component ---------- */

class FileField extends BaseDisplayItem<FileItem[], Item, HTMLElement> {
    constructor(props: Props) {
        super(props)

        this.state = {
            // BaseDisplayItem keeps state.value, but this component historically used its own array state.
            // We keep that behavior, and ALSO keep base value in sync via updateValue when needed.
            value: Array.isArray(props.value) ? props.value : [],
            signing: false,
            deleteFile: false,
            showUpload: false,
            dataSignature: '',
        }

        this.addFile = this.addFile.bind(this)
        this.renderDeleteModal = this.renderDeleteModal.bind(this)
        this.doPreview = this.doPreview.bind(this)
        this.handleSaveSignature = this.handleSaveSignature.bind(this)
    }

    /* ---------- Helpers ---------- */

    getValue() {
        // legacy: return ids
        return (this.state.value ?? []).map((f) => f.id)
    }

    private hasLimitations(): boolean {
        return Boolean((this.props as Props).item?.settings?.file_types)
    }

    private getLimitations(): string[] {
        const fileTypes = (this.props as Props).item?.settings?.file_types
        return Array.isArray(fileTypes) ? fileTypes : []
    }

    private showPreview(): boolean {
        return this.getSetting<boolean>('file_preview', {
            source: 'item',
            defaultValue: false,
        })
    }

    private showDirect(): boolean {
        return this.getSetting<boolean>('preview_direct', {
            source: 'item',
            defaultValue: false,
        })
    }

    private isDocument(): boolean {
        return (
            this.hasLimitations() && this.getLimitations().includes('documents')
        )
    }

    private getMaxWidth(): string {
        const w = (this.props as Props).item?.settings?.preview_width
        return w && w.length > 0 ? w : '200px'
    }

    private getPreviewHeight(): string {
        const h = (this.props as Props).item?.settings?.preview_height
        return h && h.length > 0 ? h : '200px'
    }

    private isImage(file: FileItem) {
        return Boolean(file?.mime?.includes('image'))
    }

    private getDownloadUrlById(fileId: string | number) {
        const baseUrl = (this.props as Props).kedo.api().getBaseUrl()
        return `${baseUrl}/file/${fileId}/download`
    }

    /* ---------- Preview ---------- */

    private doPreview(file: FileItem) {
        const props = this.props as Props
        if (!file) return null

        if (this.showDirect() && this.isImage(file)) {
            return (
                <Box
                    component="img"
                    sx={{
                        ml: 1,
                        float: 'right',
                        maxHeight: this.getPreviewHeight(),
                        maxWidth: this.getMaxWidth(),
                        height: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        bgcolor: 'background.paper',
                    }}
                    src={this.getDownloadUrlById(file.id)}
                />
            )
        }

        return (
            <Suspense fallback={<CircularProgress size={20} />}>
                <FilePreviewModal
                    kedo={props.kedo}
                    files={this.state.value}
                    currentIndex={this.state.value.findIndex(
                        (it) => it.id === file.id
                    )}
                    showButton={true}
                />
            </Suspense>
        )
    }

    /* ---------- Mutations ---------- */

    private addFile(file: FileItem) {
        const next = [...this.state.value, file]
        this.setState({ showUpload: false, value: next })

        // if you need to propagate to parent as array:
        ;(this.props as Props).changeValue?.(
            (this.props as Props).item.id,
            next,
            (this.props as Props).item
        )
    }

    private renderDeleteModal(fileId: string | number) {
        const props = this.props as Props
        let url = props.kedo.api().getFileEndpoint()

        if (props.dossier?.id) {
            url = `${url}/${fileId}/detach/${props.dossier.id}`
        } else {
            url = `${url}/${fileId}`
        }

        return (
            <ConfirmDeleteDialog
                onSuccess={() => {
                    const next = this.state.value.filter(
                        (it) => it.id !== fileId
                    )
                    this.setState({
                        deleteFile: false,
                        value: next,
                    })
                    props.changeValue?.(props.item.id, next, props.item)
                }}
                kedo={props.kedo}
                onClose={() => this.setState({ deleteFile: false })}
                url={url}
            />
        )
    }

    private handleSaveSignature(dataURL: string) {
        this.setState({ showUpload: true, dataSignature: dataURL })
    }

    /* ---------- Render: SHOW ---------- */

    renderShow(): React.ReactNode {
        const props = this.props as Props

        if (!props.value || !Array.isArray(props.value)) return ''

        const showPreview = this.showPreview()

        if (
            this.state.value.length <= 0 &&
            props.custom_layout === 'row_input'
        ) {
            return ''
        }

        if (this.state.value.length <= 0) {
            return (
                <Box
                    component="span"
                    sx={{
                        color: 'text.disabled',
                    }}
                >
                    {this.props.kedo.t('No files found')}
                </Box>
            )
        }

        return (
            <List
                disablePadding
                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                {this.state.value.map((file) => (
                    <ListItem
                        key={`fg-${file.id}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexDirection: 'column',
                            padding: '0.75rem 1.25rem',
                            backgroundColor: '#fff',
                            border: '1px solid rgba(0, 0, 0, 0.125)',
                        }}
                    >
                        <Box>
                            <FontAwesomeIcon icon={faFile} /> {file.filename}{' '}
                            {file.description && (
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'text.disabled',
                                        fontWeight: '400',
                                        fontSize: '0.875em',
                                    }}
                                >
                                    ({file.description})
                                </Box>
                            )}
                        </Box>
                        <Box
                            sx={{
                                ml: 'auto',
                                mt: 1,
                            }}
                        >
                            {this.isDocument() ? (
                                <StyledButton
                                    dense
                                    invert
                                    onClick={() =>
                                        this.setState({ signing: true })
                                    }
                                    variant={'outlined'}
                                    color="secondary"
                                    size={'small'}
                                >
                                    <FontAwesomeIcon icon={faSignature} />{' '}
                                    {this.state.signing ? (
                                        <LoadingDefault />
                                    ) : null}
                                </StyledButton>
                            ) : null}

                            {showPreview ? this.doPreview(file) : null}
                            <Tooltip title={props.kedo.t('Download item')}>
                                <Link
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    href={this.getDownloadUrlById(file.id)}
                                    underline="none"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        border: 'none',
                                        color: 'text.secondary',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <FontAwesomeIcon
                                        onClick={(e) => e.stopPropagation()}
                                        icon={faDownload}
                                    />
                                </Link>
                            </Tooltip>
                        </Box>
                    </ListItem>
                ))}
            </List>
        )
    }

    /* ---------- Render: EDIT ---------- */

    renderEdit(): React.ReactNode {
        const props = this.props as Props
        const kedo = props.kedo
        const timestamp = new Date().toLocaleString()

        return (
            <Box ref={this.innerRef}>
                {this.state.deleteFile
                    ? this.renderDeleteModal(this.state.deleteFile)
                    : null}

                {this.state.showUpload ? (
                    <NewFile
                        onClose={() => this.setState({ showUpload: false })}
                        accepts={
                            this.hasLimitations() ? this.getLimitations() : null
                        }
                        multiple={props.item.settings?.multiple}
                        onSuccess={this.addFile}
                        kedo={kedo}
                        name={`Signature ${timestamp}`}
                        description={`Signature ${timestamp}`}
                        dataSignature={this.state.dataSignature}
                    />
                ) : null}

                {props.item.settings?.signature ? (
                    <Signature onSave={this.handleSaveSignature} />
                ) : (
                    <StyledButton
                        title={kedo.t('Upload file')}
                        size={
                            props.custom_layout === 'row_input'
                                ? 'small'
                                : 'medium'
                        }
                        variant="outlined"
                        invert
                        color={this.isInvalid() ? 'error' : 'secondary'}
                        onClick={() => this.setState({ showUpload: true })}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <FontAwesomeIcon
                            style={{ marginRight: 8 }}
                            icon={faPlus}
                        />
                        {kedo.t('Upload file')}
                    </StyledButton>
                )}

                {props.custom_layout !== 'row_input' ? (
                    <Box sx={{ my: 2 }} />
                ) : null}

                {props.custom_layout !== 'row_input' &&
                this.state.value.length <= 0 ? (
                    <Box
                        component="span"
                        sx={{
                            color: 'text.disabled',
                        }}
                    >
                        {kedo.t('No files found')}
                    </Box>
                ) : (
                    <List
                        disablePadding
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {this.state.value.map((file) => (
                            <ListItem
                                key={`${props.item.id}-f-${file.id}`}
                                divider
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    padding: '0.75rem 1.25rem',
                                    backgroundColor: '#fff',
                                    border: '1px solid rgba(0, 0, 0, 0.125)',
                                }}
                            >
                                <Box>
                                    <FontAwesomeIcon icon={faFile} />{' '}
                                    {file.filename}{' '}
                                    {file.description && (
                                        <Box
                                            component="span"
                                            sx={{
                                                color: 'text.disabled',
                                                fontWeight: '400',
                                                fontSize: '0.875em',
                                            }}
                                        >
                                            ({file.description})
                                        </Box>
                                    )}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{ ml: 'auto', mt: 1 }}
                                >
                                    <Tooltip
                                        title={props.kedo.t('Download item')}
                                    >
                                        <StyledButton
                                            component="a"
                                            dense
                                            invert
                                            variant={'outlined'}
                                            color="secondary"
                                            size={'small'}
                                            title={kedo.t('Download item')}
                                            target={'_blank'}
                                            href={this.getDownloadUrlById(
                                                file.id
                                            )}
                                            sx={{ border: 'none' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </StyledButton>
                                    </Tooltip>
                                    &nbsp;
                                    {kedo.isAdminOrEnvironmentAdmin() ||
                                    kedo
                                        .env()
                                        .hasCredential('kedo.file.delete') ? (
                                        <StyledButton
                                            dense
                                            invert
                                            title={kedo.t('Detach')}
                                            onClick={() =>
                                                this.setState({
                                                    deleteFile: file.id,
                                                })
                                            }
                                            size={'small'}
                                            variant={'outlined'}
                                            color="error"
                                        >
                                            <FontAwesomeIcon icon={faUnlink} />
                                        </StyledButton>
                                    ) : null}
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                )}

                {this.isInvalid() ? (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: 'block', mt: 1 }}
                    >
                        {this.getErrorText()}
                    </Typography>
                ) : null}
            </Box>
        )
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        const props = this.props as Props

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={
                    // for search, legacy used props.value as text
                    (props.value as unknown as string) ?? ''
                }
                onChange={(e) =>
                    props.changeValue?.(
                        props.item.id,
                        e.target.value as any,
                        props.item
                    )
                }
                onKeyPress={
                    props.onKeyPress
                        ? (e) => props.onKeyPress!(props.item.id, e)
                        : undefined
                }
            />
        )
    }

    /* ---------- Extra legacy behavior: list mode direct previews ---------- */

    render(): React.ReactNode {
        const props = this.props as Props

        if (props.mode === 'list' && this.showPreview() && this.showDirect()) {
            if (
                !props.value ||
                !Array.isArray(props.value) ||
                props.value.length <= 0
            ) {
                return null
            }

            return (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {this.state.value.map((file) => (
                        <Box
                            key={`preview-${file.id}`}
                            component="img"
                            sx={{
                                ml: 1,
                                float: 'right',
                                maxHeight: this.getPreviewHeight(),
                                maxWidth: this.getMaxWidth(),
                                height: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 0.5,
                                bgcolor: 'background.paper',
                            }}
                            src={this.getDownloadUrlById(file.id)}
                        />
                    ))}
                </Box>
            )
        }

        return super.render()
    }
}

export default FileField
